import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Camera, MapPin, Target, CloudSlash, ArrowsClockwise, WifiHigh, ShareFat, Buildings, Users, PersonSimpleWalk } from '@phosphor-icons/react';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/51d11d5a-6fe7-4a62-ab23-fd9ad4ee26cb/images/d0423a9e91a707c51f348c33371e5294b20e596442a5b58835568df851b8ac67.png';

export default function Landing() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!loading && user) navigate('/app', { replace: true });
    }, [user, loading, navigate]);

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/app';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    /** "Got a link from your homeowner?" — accepts either a full
     *  /shared/<id> URL or just the bare short_id and routes the
     *  builder straight into the portal. Surfaces a tiny inline error
     *  when the input doesn't look like a DefectR share. */
    const [shareInput, setShareInput] = useState('');
    const [shareErr, setShareErr] = useState('');
    const handleShareJump = (e) => {
        e?.preventDefault?.();
        const raw = (shareInput || '').trim();
        if (!raw) { setShareErr('Paste your share link first.'); return; }
        // Match either full URL like /shared/<id>?r=<key> or bare id.
        const urlMatch = raw.match(/\/shared\/([A-Za-z0-9]+)(?:\?([^#]*))?/);
        if (urlMatch) {
            const target = `/shared/${urlMatch[1]}` + (urlMatch[2] ? `?${urlMatch[2]}` : '');
            navigate(target);
            return;
        }
        const bare = raw.match(/^[A-Za-z0-9]{8}$/);
        if (bare) { navigate(`/shared/${bare[0]}`); return; }
        setShareErr('That doesn\'t look like a DefectR share link. Paste the full URL or the share code.');
    };

    return (
        <div className="min-h-screen bg-white text-[#09090B]">
            {/* Top bar */}
            <header className="border-b border-[#18181B]">
                <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
                    <div className="flex items-center">
                        <img
                            src="/defectr-logo.png"
                            alt="DefectR — Defects Rectified"
                            className="h-10 sm:h-12 w-auto object-contain"
                        />
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <Link
                            to="/developer"
                            data-testid="top-nav-developer-link"
                            className="hidden sm:inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] hover:text-[#1787AB] transition-colors duration-150"
                        >
                            For housebuilders
                            <ArrowRight size={12} weight="bold" />
                        </Link>
                        <button
                            onClick={handleLogin}
                            data-testid="top-login-btn"
                            className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-[#18181B] hover:bg-[#09090B] hover:text-white transition-all duration-150 active:scale-95"
                        >
                            Sign in
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-3">
                    <div className="label-tiny mb-4">For new-build homeowners · Defects Rectified</div>
                    <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-tight">
                        <span className="block">Snag it.</span>
                        <span className="inline-block bg-[#1FA5CF] text-white px-3 py-0.5 my-2 -rotate-[0.5deg]">Prove it.</span>
                        <span className="block">Rectify it.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[#52525B] max-w-xl">
                        Document every defect in your new build, room by room. Drop pins on your floorplan,
                        snap photos, and ship a watertight report to your builder in one tap.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleLogin}
                            data-testid="hero-login-btn"
                            className="group inline-flex items-center justify-center gap-2 bg-[#1FA5CF] text-white font-mono text-sm uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#1787AB] transition-all duration-150 active:scale-95"
                        >
                            Continue with Google
                            <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                        </button>
                        <div className="flex flex-col items-start sm:items-start justify-center gap-0.5 text-xs text-[#52525B] font-mono uppercase tracking-[0.18em] px-2 leading-relaxed">
                            <span>Free to start</span>
                            <span className="text-[#A1A1AA]">Runs in your browser</span>
                        </div>
                    </div>

                    {/* Builder shortcut — separated by a thin rule so it
                        reads as a parallel path, not a CTA competition.
                        Most homeowners ignore it; builders googling
                        "DefectR" land here with no other route into the
                        product. */}
                    <form
                        onSubmit={handleShareJump}
                        className="mt-8 pt-5 border-t border-dashed border-[#E4E4E7] max-w-xl"
                        data-testid="share-jump-form"
                    >
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#52525B]">
                            <ShareFat size={12} weight="bold" color="#1FA5CF" />
                            Got a link from your homeowner?
                        </div>
                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                            <input
                                value={shareInput}
                                onChange={(e) => { setShareInput(e.target.value); setShareErr(''); }}
                                placeholder="Paste share link or code (e.g. uVGYCNtK)"
                                data-testid="share-jump-input"
                                className="flex-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2.5 text-sm font-mono"
                                autoComplete="off"
                                spellCheck={false}
                            />
                            <button
                                type="submit"
                                data-testid="share-jump-btn"
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border border-[#18181B] text-[#09090B] hover:bg-[#09090B] hover:text-white active:scale-95 transition-all duration-150"
                            >
                                Open report
                                <ArrowRight size={14} weight="bold" />
                            </button>
                        </div>
                        {shareErr && (
                            <p className="mt-2 text-xs text-[#E53935]" data-testid="share-jump-error">{shareErr}</p>
                        )}
                    </form>
                </div>
                <div className="lg:col-span-2 relative">
                    <div className="grid-border bg-[#F4F4F5] aspect-[4/5] relative overflow-hidden">
                        <img src={HERO_IMG} alt="Floorplan with pin" className="w-full h-full object-cover" />
                        <div className="absolute top-4 left-4 bg-white border border-[#18181B] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                            Plot 14 · Oak Drive
                        </div>
                        <div className="absolute bottom-4 right-4 bg-[#1FA5CF] text-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                            23 defects
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature grid */}
            <section className="border-t border-[#18181B]">
                <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        { icon: Camera, label: 'Photo Evidence', copy: 'Capture or upload snaps for every defect, tagged by room. Draw arrows & circles right on the photo to point out exactly where the issue is.' },
                        { icon: MapPin, label: 'Pin Your Plan', copy: 'Upload a floorplan and drop pins exactly where issues are.' },
                        { icon: Target, label: 'Spirit Level', copy: 'Turn your phone into a calibrated level using its gyroscope.' },
                        { icon: PersonSimpleWalk, label: 'Walk-Around Mode', copy: 'Two-tap quick capture — walk the property snapping photos, then tag them all at once. Paid tiers get AI-suggested titles.' },
                    ].map(({ icon: Icon, label, copy }, idx) => (
                        <div
                            key={label}
                            className={`p-6 border-[#18181B] ${idx < 3 ? 'sm:border-r' : ''} ${idx < 2 ? 'border-b sm:border-b' : 'border-b sm:border-b-0'} lg:border-b-0 fade-up`}
                            style={{ animationDelay: `${idx * 80}ms` }}
                        >
                            <Icon size={28} weight="duotone" color="#1FA5CF" />
                            <div className="label-tiny mt-4">{label}</div>
                            <p className="mt-2 text-sm text-[#09090B]">{copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Built for the building site — offline-first proof */}
            <section className="border-t border-[#18181B] bg-[#09090B] text-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
                    <div data-testid="offline-pitch">
                        <div className="label-tiny mb-3 text-[#1FA5CF]">Works on a building site</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                            One bar of signal? <br />
                            <span className="text-[#1FA5CF]">Zero bars?</span> Doesn&apos;t matter.
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-[#A1A1AA] max-w-md">
                            Snag a half-built basement with no signal. Walk between unfinished
                            plots through patchy 4G. The app keeps recording. The moment you&apos;re
                            back in range, every defect, photo and edit syncs in the background.
                        </p>
                        <ul className="mt-6 space-y-3 text-sm">
                            {[
                                ['Captures defects offline — even on first launch of the day.', WifiHigh],
                                ['Photos compress and queue locally until reconnect.', CloudSlash],
                                ['One quiet message confirms the sync — “3 defects synced.” That\u2019s it.', ArrowsClockwise],
                            ].map(([copy, Icon]) => (
                                <li key={copy} className="flex items-start gap-3">
                                    <Icon size={18} weight="bold" color="#1FA5CF" className="mt-0.5 flex-shrink-0" />
                                    <span>{copy}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Faux phone chrome showing the real OfflineIndicator pill */}
                    <div className="relative mx-auto w-full max-w-sm">
                        <div className="bg-white text-[#09090B] border-4 border-[#27272A] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#1FA5CF]/20">
                            {/* Notch */}
                            <div className="bg-[#27272A] h-6 flex justify-center items-end pb-1">
                                <div className="w-20 h-3 bg-black rounded-b-xl" />
                            </div>
                            {/* App header mock — matches AppHeader.jsx */}
                            <div className="border-b border-[#18181B] px-3 py-2.5 flex items-center gap-2">
                                <img src="/defectr-logo.png" alt="" className="h-6 w-auto" />
                                <div className="flex-1" />
                                {/* Real OfflineIndicator markup */}
                                <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#FEE2E2] border border-[#E53935] text-[#991B1B] font-mono text-[10px] uppercase tracking-wider">
                                    <CloudSlash size={12} weight="bold" />
                                    <span>Offline</span>
                                    <span className="tabular-nums">· 3</span>
                                </div>
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono font-semibold uppercase tracking-[0.14em] border bg-[#F4F4F5] text-[#52525B] border-[#D4D4D8]">
                                    Foundation
                                </span>
                            </div>
                            {/* Project body mock */}
                            <div className="p-4 space-y-3 bg-[#FAFAFA]">
                                <div className="label-tiny">Plot 14 · Oak Drive</div>
                                {[
                                    ['Hairline crack — kitchen ceiling', 'Outstanding', '#1FA5CF', 'pending'],
                                    ['Skirting gap — landing', 'Outstanding', '#1FA5CF', 'pending'],
                                    ['Tap drip — en-suite', 'Outstanding', '#1FA5CF', 'pending'],
                                    ['Door rub — main bedroom', 'Completed', '#10B981', null],
                                ].map(([title, status, color, pendingFlag]) => (
                                    <div
                                        key={title}
                                        className={`bg-white border border-[#E4E4E7] px-3 py-2 flex items-center gap-3 ${pendingFlag ? 'border-l-2 border-l-[#1FA5CF]' : ''}`}
                                    >
                                        <div
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ background: color }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-medium truncate">{title}</div>
                                            <div className="text-[10px] text-[#71717A] font-mono uppercase tracking-wider mt-0.5">
                                                {status}
                                                {pendingFlag && <span className="ml-2 text-[#1FA5CF]">· Queued</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="text-[10px] text-[#71717A] font-mono uppercase tracking-wider pt-1 text-center">
                                    3 changes will sync when back online
                                </div>
                            </div>
                        </div>
                        {/* Soft glow */}
                        <div className="absolute inset-0 -z-10 blur-3xl bg-[#1FA5CF]/20 rounded-full" />
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                <div className="label-tiny mb-2">How it works</div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Three simple steps.</h2>
                <div className="mt-10 grid sm:grid-cols-3 gap-0 border border-[#18181B]">
                    {[
                        ['01', 'Create a property', 'Add rooms and upload your floorplan if you have one.'],
                        ['02', 'Walk & snag', 'Photograph defects, drop pins, toggle status as fixes happen.'],
                        ['03', 'Send the report', 'Export a clean PDF or share directly by email.'],
                    ].map(([n, h, c], i) => (
                        <div key={n} className={`p-6 ${i < 2 ? 'sm:border-r border-[#18181B]' : ''} ${i < 2 ? 'border-b sm:border-b-0 border-[#18181B]' : ''}`}>
                            <div className="font-mono text-5xl font-bold text-[#1FA5CF]">{n}</div>
                            <div className="font-display font-semibold mt-4">{h}</div>
                            <p className="text-sm text-[#52525B] mt-2">{c}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing */}
            <section className="border-t border-[#18181B] bg-[#FAFAFA]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                    <div className="label-tiny mb-2">Pricing</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                        Honest, one-off pricing.
                    </h2>
                    <p className="mt-3 text-sm text-[#52525B] max-w-xl">
                        Try it free — pay only if you need to go further. No subscriptions, ever.
                    </p>

                    <div
                        className="mt-10 grid sm:grid-cols-3 gap-0 border border-[#18181B] bg-white"
                        data-testid="pricing-tiers"
                    >
                        {[
                            {
                                name: 'Foundation',
                                price: 'Free',
                                priceSuffix: null,
                                tagline: 'Try it on your home',
                                features: [
                                    '1 property',
                                    'Up to 10 defects',
                                    'Photo mark-up (arrows / circles / freehand)',
                                    'Walk-around quick capture',
                                    'Floorplan pins',
                                    '1 PDF export',
                                    '3 free AI defect analyses (lifetime)',
                                ],
                                cta: null,
                                accent: false,
                            },
                            {
                                name: 'Rectify',
                                price: '£14.99',
                                priceSuffix: 'per property',
                                tagline: 'For your snagging window',
                                features: [
                                    'Everything in Foundation',
                                    'Unlimited defects on the unlocked property',
                                    'Unlimited AI defect analyses & walk-around AI Suggest',
                                    'Public share link + QR for builder',
                                    'Builder response portal (approve / reject / dispute)',
                                    'Unlimited branded PDF exports',
                                    'Priority email delivery via Resend',
                                ],
                                cta: 'Most popular',
                                accent: true,
                            },
                            {
                                name: 'Surveyor',
                                price: '£149.99',
                                priceSuffix: 'lifetime',
                                tagline: 'Multiple homes or properties',
                                features: [
                                    'Everything in Rectify',
                                    'Unlimited properties',
                                    'One-time payment · no subscription',
                                    'Pays for itself at ~10 homes',
                                    'Ideal for landlords, surveyors, family & friends',
                                ],
                                cta: null,
                                accent: false,
                            },
                        ].map((tier, i) => (
                            <div
                                key={tier.name}
                                className={`p-6 sm:p-7 relative ${i < 2 ? 'sm:border-r border-[#18181B]' : ''} ${i < 2 ? 'border-b sm:border-b-0 border-[#18181B]' : ''} ${tier.accent ? 'bg-[#F8FCFE]' : ''}`}
                                data-testid={`pricing-${tier.name.toLowerCase()}`}
                            >
                                {tier.cta && (
                                    <div className="absolute top-0 right-0 bg-[#1FA5CF] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]">
                                        {tier.cta}
                                    </div>
                                )}
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
                                    {tier.name}
                                </div>
                                <div className="mt-3 flex items-baseline gap-1.5">
                                    <span className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#09090B]">
                                        {tier.price}
                                    </span>
                                    {tier.priceSuffix && (
                                        <span className="text-xs text-[#52525B] font-mono uppercase tracking-wider">
                                            {tier.priceSuffix}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-xs text-[#52525B]">{tier.tagline}</p>
                                <ul className="mt-5 space-y-2 text-sm">
                                    {tier.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2">
                                            <span className="mt-1.5 w-1 h-1 bg-[#1FA5CF] flex-shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-6 text-xs text-[#52525B] font-mono uppercase tracking-[0.18em]">
                        One-off payments · No subscription · Pay only when you upgrade
                    </p>
                </div>
            </section>

            {/* For housebuilders — dedicated B2B funnel band on the
                landing page. Sits between consumer pricing and the trust
                strip so the homeowner story is intact but housebuilders
                find a clear, branded entry point without scrolling to
                the footer. Distinct dark background so it visually
                pivots the conversation from "you" to "your team". */}
            <section className="bg-[#09090B] text-white" data-testid="b2b-band">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-5 gap-8 items-center">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1FA5CF] mb-4">
                            <Buildings size={14} weight="duotone" />
                            For housebuilders
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                            <span className="block">Run a development?</span>
                            <span className="inline-block bg-[#1FA5CF] text-white px-3 py-0.5 mt-2">
                                Snag it at scale.
                            </span>
                        </h2>
                        <p className="mt-5 text-base text-[#A1A1AA] max-w-xl leading-relaxed">
                            Multi-seat licences for your aftercare team, branded PDFs that go out
                            under your identity, CSV bulk-import of every plot, and a daily digest
                            so site managers never miss a homeowner-logged defect.
                        </p>
                        <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <BLi>25 seats per licence</BLi>
                            <BLi>Your logo on every report</BLi>
                            <BLi>CSV → DefectR in seconds</BLi>
                            <BLi>One annual invoice, Stripe-managed</BLi>
                        </ul>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/developer"
                                data-testid="b2b-band-cta"
                                className="group inline-flex items-center justify-center gap-2 bg-[#1FA5CF] text-white font-mono text-sm uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#1787AB] transition-all duration-150 active:scale-95"
                            >
                                See Developer tier
                                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a
                                href="mailto:support@defectr.co.uk?subject=DefectR%20Enterprise%20enquiry"
                                data-testid="b2b-band-enterprise"
                                className="inline-flex items-center justify-center gap-2 border border-[#52525B] text-[#A1A1AA] hover:text-white hover:border-white font-mono text-sm uppercase tracking-[0.2em] px-6 py-4 transition-all duration-150 active:scale-95"
                            >
                                Enterprise enquiry
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <div className="border-2 border-[#1FA5CF] bg-[#0E1B26] p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1FA5CF] mb-2">
                                Developer · Annual
                            </div>
                            <div className="font-display text-5xl font-bold">£4,800<span className="text-base font-mono text-[#A1A1AA] uppercase tracking-wider ml-2">/yr</span></div>
                            <div className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                                25 seats · £192 per seat
                            </div>
                            <div className="mt-5 pt-5 border-t border-[#27272A] flex items-center gap-3">
                                <Users size={18} weight="duotone" color="#1FA5CF" />
                                <p className="text-xs text-[#A1A1AA] leading-snug">
                                    Need 100+ seats or a PO? <a href="mailto:support@defectr.co.uk" className="underline text-white">Talk to us about Enterprise</a>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust strip — borrowed credibility from named sub-processors.
                Plain text on a desaturated band so it reads as transparency,
                not as advertising. Privacy page covers the full breakdown. */}
            <section
                className="border-t border-[#E4E4E7] bg-[#FAFAFA]"
                data-testid="trust-strip"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#52525B]">
                            Built on infrastructure you already trust
                        </div>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#09090B]">
                            <TrustItem
                                label="Payments"
                                provider="Stripe"
                                testId="trust-stripe"
                            />
                            <span aria-hidden className="text-[#D4D4D8]">·</span>
                            <TrustItem
                                label="Email"
                                provider="Resend"
                                testId="trust-resend"
                            />
                            <span aria-hidden className="text-[#D4D4D8]">·</span>
                            <TrustItem
                                label="AI analysis"
                                provider="Google Gemini"
                                testId="trust-gemini"
                            />
                            <span aria-hidden className="text-[#D4D4D8]">·</span>
                            <TrustItem
                                label="Sign-in"
                                provider="Google OAuth"
                                testId="trust-google"
                            />
                        </div>
                    </div>
                    <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                        Full sub-processor list and what they handle:&nbsp;
                        <Link
                            to="/privacy"
                            data-testid="trust-strip-privacy-link"
                            className="underline hover:text-[#1787AB] transition-colors duration-150"
                        >
                            Privacy &amp; security
                        </Link>
                        <span className="mx-2 text-[#D4D4D8]">·</span>
                        <Link
                            to="/terms"
                            data-testid="trust-strip-terms-link"
                            className="underline hover:text-[#1787AB] transition-colors duration-150"
                        >
                            Terms &amp; refunds
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#18181B]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-xs font-mono uppercase tracking-[0.2em] text-[#52525B]">
                    <span>© DefectR Ltd</span>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/developer"
                            data-testid="footer-developer-link"
                            className="underline hover:text-[#1787AB] transition-colors duration-150"
                        >
                            For housebuilders
                        </Link>
                        <Link
                            to="/privacy"
                            data-testid="footer-privacy-link"
                            className="underline hover:text-[#1787AB] transition-colors duration-150"
                        >
                            Privacy &amp; security
                        </Link>
                        <Link
                            to="/terms"
                            data-testid="footer-terms-link"
                            className="underline hover:text-[#1787AB] transition-colors duration-150"
                        >
                            Terms &amp; refunds
                        </Link>
                    </div>
                    <span>Built for new-build owners</span>
                </div>
            </footer>
        </div>
    );
}

function TrustItem({ label, provider, testId }) {
    return (
        <span
            data-testid={testId}
            className="inline-flex items-baseline gap-1.5"
        >
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#52525B]">
                {label}
            </span>
            <span className="font-display font-semibold tracking-tight">
                {provider}
            </span>
        </span>
    );
}

/** Tiny bullet row used in the "For housebuilders" B2B band. */
function BLi({ children }) {
    return (
        <li className="flex items-start gap-2 text-[#E4E4E7]">
            <span className="mt-1.5 w-1 h-1 bg-[#1FA5CF] flex-shrink-0" />
            <span>{children}</span>
        </li>
    );
}

